import React, { useState, useEffect } from 'react';
import { Plus, Lightbulb, Edit2, Tag, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { IPEntity, Tag as TagType, Staff } from '../types';
import { IPModal } from '../components/IPModal';
import { IPViewModal } from '../components/IPViewModal';

interface DeleteConfirmModalProps {
  ip: IPEntity;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

function DeleteConfirmModal({ ip, onClose, onConfirm, loading }: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Delete IP</h2>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete "{ip.title}"? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {loading ? 'Deleting...' : 'Delete IP'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CollapsibleTagsProps {
  tags: TagType[];
}

function CollapsibleTags({ tags }: CollapsibleTagsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const validTags = tags.filter(tag => tag && tag.id && tag.name);
  const visibleTags = isExpanded ? validTags : validTags.slice(0, 3);
  const remainingCount = validTags.length - 3;

  if (validTags.length <= 3) {
    return (
      <div className="flex flex-wrap gap-1">
        {validTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
          >
            <Tag className="w-3 h-3 mr-1" />
            {tag.name}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {visibleTags.map((tag) => (
          <span
            key={tag.id}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
          >
            <Tag className="w-3 h-3 mr-1" />
            {tag.name}
          </span>
        ))}
        {!isExpanded && remainingCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(true);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronDown className="w-3 h-3 mr-1" />
            +{remainingCount} more
          </button>
        )}
        {isExpanded && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(false);
            }}
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            <ChevronUp className="w-3 h-3 mr-1" />
            Show less
          </button>
        )}
      </div>
    </div>
  );
}

export function IP() {
  const [ipEntities, setIPEntities] = useState<IPEntity[]>([]);
  const [allTags, setAllTags] = useState<TagType[]>([]);
  const [allStaff, setAllStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewIP, setShowNewIP] = useState(false);
  const [selectedIP, setSelectedIP] = useState<IPEntity | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [ipToDelete, setIPToDelete] = useState<IPEntity | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ipResponse, tagsResponse, staffResponse] = await Promise.all([
        supabase
          .from('ip_entities')
          .select(`
            *,
            descriptions:ip_descriptions (
              id,
              content,
              display_order,
              created_at
            ),
            images:ip_images (
              id,
              url,
              display_order,
              created_at
            ),
            links:ip_links (
              id,
              url,
              title,
              created_at
            ),
            notes:ip_notes (
              id,
              content,
              created_at
            ),
            tags:ip_tags (
              tag:tags (
                id,
                name
              )
            ),
            staff:ip_staff (
              staff:staff (
                id,
                name,
                role,
                experience_level
              )
            )
          `)
          .order('created_at', { ascending: false }),
        supabase
          .from('tags')
          .select('*')
          .order('name'),
        supabase
          .from('staff')
          .select('*')
          .order('name')
      ]);

      if (ipResponse.error) throw ipResponse.error;
      if (tagsResponse.error) throw tagsResponse.error;
      if (staffResponse.error) throw staffResponse.error;

      const transformedIP: IPEntity[] = ipResponse.data.map(ip => ({
        id: ip.id,
        title: ip.title,
        descriptions: ip.descriptions,
        images: ip.images,
        links: ip.links,
        notes: ip.notes,
        tags: ip.tags.map((t: any) => t.tag),
        staff: ip.staff.map((s: any) => s.staff),
        created_at: ip.created_at,
        updated_at: ip.updated_at
      }));

      setIPEntities(transformedIP);
      setAllTags(tagsResponse.data);
      setAllStaff(staffResponse.data);
    } catch (error) {
      console.error('Error loading IP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ip: IPEntity) => {
    setDeleteLoading(true);
    try {
      // Delete all images from storage
      for (const image of ip.images) {
        const url = new URL(image.url);
        const filePath = url.pathname.split('/').pop();
        if (filePath) {
          await supabase.storage
            .from('ip-images')
            .remove([filePath]);
        }
      }

      // Delete IP entity (cascade will handle related records)
      const { error: deleteError } = await supabase
        .from('ip_entities')
        .delete()
        .eq('id', ip.id);

      if (deleteError) throw deleteError;

      // Refresh the data
      await loadData();
    } catch (error) {
      console.error('Error deleting IP:', error);
    } finally {
      setDeleteLoading(false);
      setIPToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading IP data...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Lightbulb className="w-8 h-8 text-indigo-600 mr-3" />
            <h1 className="text-2xl font-bold text-gray-900">Intellectual Property</h1>
          </div>
          <button
            onClick={() => {
              setShowNewIP(true);
              setIsEditing(true);
            }}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            New IP
          </button>
        </div>

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tags
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ipEntities.map((ip) => (
                  <tr
                    key={ip.id}
                    onClick={() => {
                      setSelectedIP(ip);
                      setIsEditing(false);
                    }}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{ip.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <CollapsibleTags tags={ip.tags} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(ip.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedIP(ip);
                            setIsEditing(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIPToDelete(ip);
                          }}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {selectedIP && !isEditing && (
          <IPViewModal
            ip={selectedIP}
            onClose={() => setSelectedIP(null)}
          />
        )}

        {(showNewIP || (selectedIP && isEditing)) && (
          <IPModal
            ip={selectedIP || undefined}
            allTags={allTags}
            allStaff={allStaff}
            onClose={() => {
              setShowNewIP(false);
              setSelectedIP(null);
              setIsEditing(false);
            }}
            onSave={() => {
              loadData();
              setShowNewIP(false);
              setSelectedIP(null);
              setIsEditing(false);
            }}
            isNew={!selectedIP}
          />
        )}

        {ipToDelete && (
          <DeleteConfirmModal
            ip={ipToDelete}
            onClose={() => setIPToDelete(null)}
            onConfirm={() => handleDelete(ipToDelete)}
            loading={deleteLoading}
          />
        )}
      </div>
    </div>
  );
}